<template>
  <div class="d-flex flex-column" :class="$style.sectionLayout">
    <div class="d-flex flex-column gap-2">
      <AnnouncementBanner
        v-for="announcement in announcements"
        :id="announcement.id"
        :key="announcement.id"
        :title="announcement.title"
      />
    </div>

    <div :class="$style.actionListWrapper">
      <ActionList
        :should-show-steps-registration="props.shouldShowStepsRegistration"
        :user-platform="props.userPlatform"
        :loading="props.dashboardLoading"
        @select="handleActionSelect"
      />
    </div>

    <div :class="$style.platformSections">
      <template v-for="section in orderedPlatformSections" :key="section">
        <div
          v-if="section === 'message'"
          data-testid="top-auth-section-message"
          :class="$style.platformSection"
        >
          <SectionTitle type="message" />
          <FilledTab
            :tabs="messageTabs"
            :model-value="messageInitialTab"
            :before-change="handleMessageTabBeforeChange"
          >
            <template #default="{ activeTab }">
              <ContentWithEmptyState
                :items="messageItemsForTab(activeTab.id)"
                :empty-message="
                  activeTab.id === 'agent'
                    ? 'エージェントの選考はありません。'
                    : '企業スカウトの選考はありません。'
                "
                :loading="dashboardLoading"
              >
                <template #empty>
                  <SelectionEmptyCta
                    v-if="messageEmptyStateForTab(activeTab.id)"
                    :content="messageEmptyStateForTab(activeTab.id)"
                    surface="transparent"
                    tone="light"
                    button-style="outlined"
                  />
                </template>
                <!-- TODO: shared/loading が作成されたら共通化する -->
                <div
                  v-if="dashboardLoading"
                  :class="$style.sectionLoading"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div :class="$style.sectionLoadingSpinner" role="status" />
                </div>
                <div v-else class="d-flex flex-column gap-3">
                  <MessageSummaryCard
                    v-for="item in messageItemsForTab(activeTab.id)"
                    :key="item.messageId"
                    v-bind="item"
                  />
                  <Button
                    variant="primary"
                    button-style="outlined"
                    :to="activeTab.id === 'agent' ? '/messages?platform=agent' : '/messages'"
                    class="align-self-center w-auto text-nowrap"
                  >
                    {{
                      activeTab.id === 'agent'
                        ? 'エージェントの選考管理一覧を見る'
                        : '企業スカウトの選考管理一覧を見る'
                    }}
                  </Button>
                </div>
              </ContentWithEmptyState>
            </template>
          </FilledTab>
        </div>

        <div v-else data-testid="top-auth-section-agent-recommend" :class="$style.platformSection">
          <SectionTitle type="agent" />
          <template v-if="isDirectOnly">
            <div :class="$style.directAgentRecommendCta">
              <div :class="$style.directAgentRecommendBody">
                <p
                  v-for="message in AGENT_SERVICE_GUIDE_MESSAGES"
                  :key="message"
                  :class="$style.directAgentRecommendHint"
                >
                  {{ message }}
                </p>
              </div>
              <Button
                variant="primary"
                button-style="filled"
                :to="TEASER_PATH"
                class="align-self-center rounded-pill px-4"
                @mousedown="capturePrePathOfTeaserIfNavigationIntent"
              >
                エージェントサービスのご紹介
              </Button>
            </div>
          </template>
          <AgentRecommendJobCardCarousel
            v-else
            :items="agentRecommendItems"
            :loading="dashboardLoading"
          />
        </div>
      </template>
    </div>

    <AdBanner />

    <SectionTitle type="bookmark" />
    <FilledTab
      :tabs="favoriteTabs"
      :model-value="favoriteInitialTab"
      :before-change="handleFavoriteTabBeforeChange"
    >
      <template #default="{ activeTab }">
        <ContentWithEmptyState
          :items="favoriteItemsForTab(activeTab.id)"
          :empty-message="
            activeTab.id === 'considering'
              ? '検討中の求人はありません。'
              : '気になる求人はありません。'
          "
          :loading="favoriteSectionIsLoading(activeTab.id)"
        >
          <div class="d-flex flex-column gap-3">
            <Carousel
              :key="activeTab.id"
              :items="favoriteItemsForTab(activeTab.id)"
              :loading="favoriteSectionIsLoading(activeTab.id)"
              loading-spinner-theme="light"
            />
            <Button
              variant="primary"
              button-style="outlined"
              :to="
                activeTab.id === 'considering'
                  ? '/agent/auth/consideration_jobs'
                  : '/favorites/received'
              "
              class="align-self-center w-auto text-nowrap"
            >
              {{
                activeTab.id === 'considering' ? '検討中の求人一覧を見る' : '気になる求人一覧を見る'
              }}
            </Button>
          </div>
        </ContentWithEmptyState>
      </template>
    </FilledTab>

    <SectionTitle type="jobs" />
    <FilledTab :tabs="jobsTabs">
      <template #default="{ activeTab }">
        <ContentWithEmptyState
          :items="jobsItemsForTab(activeTab.id)"
          :empty-message="jobsEmptyMessage(activeTab.id)"
          :loading="dashboardLoading"
        >
          <div class="d-flex flex-column gap-3">
            <Carousel
              :key="activeTab.id"
              :items="jobsItemsForTab(activeTab.id)"
              :loading="dashboardLoading"
              loading-spinner-theme="light"
            />
            <Button
              v-if="activeTab.id !== 'history'"
              variant="primary"
              button-style="outlined"
              :to="activeTab.id === 'agent' ? '/jobs/agent' : '/jobs/direct'"
              class="align-self-center w-auto text-nowrap"
            >
              {{
                activeTab.id === 'agent'
                  ? 'エージェント求人の一覧を見る'
                  : '企業スカウト求人の一覧を見る'
              }}
            </Button>
          </div>
        </ContentWithEmptyState>
      </template>
    </FilledTab>

    <BannerCarousel class="mt-4" />
    <RegistrationModal
      v-model="isRegistrationModalOpen"
      :should-show-steps-registration="shouldShowStepsRegistration"
      :on-submit="handleRegistrationSubmit"
    />
    <PlatformDirectModal v-model="isDirectModalOpen" />
    <AgentServiceGuideModal v-model="isAgentServiceGuideModalOpen" />
    <AgentInterviewGuideModal v-model="isAgentInterviewGuideModalOpen" />
    <EeasyMyPageGuideModal
      :model-value="props.showEeasyMyPageGuideModal ?? false"
      @update:model-value="emit('update:showEeasyMyPageGuideModal', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import type { CarouselItem } from '@/components/shared/carousel/types';
import type { ActionKey, TopAuthViewProps } from './types';

import AdBanner from '@/components/shared/ad-banner/AdBanner.vue';
import AnnouncementBanner from '@/components/shared/announcement-banner/index.vue';
import Button from '@/components/shared/button/Button.vue';
import Carousel from '@/components/shared/carousel/Carousel.vue';
import ContentWithEmptyState from '@/components/shared/empty-state/ContentWithEmptyState.vue';
import SelectionEmptyCta from '@/components/shared/empty-state/SelectionEmptyCta.vue';
import MessageSummaryCard from '@/components/shared/message/MessageSummaryCard.vue';
import AgentInterviewGuideModal from '@/components/shared/modal/AgentInterviewGuideModal.vue';
import AgentServiceGuideModal from '@/components/shared/modal/AgentServiceGuideModal.vue';
import EeasyMyPageGuideModal from '@/components/shared/modal/EeasyMyPageGuideModal.vue';
import PlatformDirectModal from '@/components/shared/modal/PlatformDirectModal.vue';
import { AGENT_SERVICE_GUIDE_MESSAGES } from '@/components/shared/modal/agentServiceGuideMessages';
import SectionTitle from '@/components/shared/section-title/SectionTitle.vue';
import FilledTab from '@/components/shared/tab/filled-tab/FilledTab.vue';
import ActionList from '@/components/top/auth/ActionList.vue';
import AgentRecommendJobCardCarousel from '@/components/top/auth/agent-recommend/AgentRecommendJobCardCarousel.vue';
import BannerCarousel from '@/components/top/auth/banner-carousel/BannerCarousel.vue';
import RegistrationModal from '@/components/top/auth/registration-modal/RegistrationModal.vue';
import { TEASER_PATH } from '@/constant/routers/path';
import {
  buildAgentSelectionEmptyState,
  buildScoutSelectionEmptyState,
} from '@/utils/messages/emptyStateCta';
import { computed, ref } from 'vue';

const props = defineProps<TopAuthViewProps>();
const emit = defineEmits<{
  (e: 'update:showEeasyMyPageGuideModal', value: boolean): void;
  (e: 'registration-completed'): void;
}>();
const favoriteSectionIsLoading = (tabId: string): boolean =>
  !!props.dashboardLoading ||
  !!props.favoriteSectionLoadingByTab?.[tabId as 'favorites' | 'considering'];

const isRegistrationModalOpen = ref(false);
const isDirectModalOpen = ref(false);
const isAgentServiceGuideModalOpen = ref(false);
const isAgentInterviewGuideModalOpen = ref(false);
const isDirectOnly = computed(() => props.userPlatform === 'direct');

type PlatformSectionKey = 'message' | 'agentRecommend';

/** ダイレクトのみは選考管理を先に。それ以外はエージェントおすすめを先に（DOM順＝読み上げ順） */
const orderedPlatformSections = computed<PlatformSectionKey[]>(() =>
  isDirectOnly.value ? ['message', 'agentRecommend'] : ['agentRecommend', 'message']
);

const messageInitialTab = computed(() => (props.userPlatform === 'agent' ? 'agent' : undefined));
const favoriteInitialTab = computed(() =>
  props.userPlatform === 'agent' ? 'considering' : undefined
);

const openAgentServiceGuideModal = (): void => {
  isAgentServiceGuideModalOpen.value = true;
};

const capturePrePathOfTeaser = (): void => {
  localStorage.setItem('prePathOfTeaser', `${window.location.pathname}${window.location.search}`);
};

/** 左クリック・中クリック（新しいタブで開く）の mousedown で戻り先を保存（click は中クリックで発火しない） */
const capturePrePathOfTeaserIfNavigationIntent = (event: MouseEvent): void => {
  if (event.button === 0 || event.button === 1) {
    capturePrePathOfTeaser();
  }
};

const handleRegistrationSubmit = async (): Promise<void> => {
  emit('registration-completed');
};

/** 選考管理: 各タブの先頭3件のみ */
const messageItemsForTab = (tabId: string) => (props.messageItemsByTab[tabId] || []).slice(0, 3);
const messageEmptyStateForTab = (tabId: string) =>
  props.messageEmptyStateByTab?.[tabId] ??
  (tabId === 'agent' ? buildAgentSelectionEmptyState(false) : buildScoutSelectionEmptyState(false));

const favoriteItemsForTab = (tabId: string): CarouselItem[] =>
  (props.favoriteItemsByTab[tabId] || []).map((item: CarouselItem) => ({
    ...item,
    actionType: 'detail-only' as const,
  }));

const jobsItemsForTab = (tabId: string): CarouselItem[] =>
  (props.jobsItemsByTab[tabId] || []).map((item: CarouselItem) => ({
    ...item,
    actionType: 'detail-only' as const,
  }));

const jobsEmptyMessage = (tabId: string): string => {
  switch (tabId) {
    case 'scout':
      return '企業スカウト求人はありません。';
    case 'agent':
      return 'エージェント求人はありません。';
    case 'history':
      return '閲覧履歴の求人はありません。';
    default:
      return '求人はありません。';
  }
};

const openDirectModal = (): void => {
  isDirectModalOpen.value = true;
};

const handleMessageTabBeforeChange = (tabId: string): boolean => {
  if (props.userPlatform === 'direct' && tabId === 'agent') {
    openAgentServiceGuideModal();
    return false;
  }
  if (props.userPlatform === 'agent' && tabId === 'direct') {
    openDirectModal();
    return false;
  }
  return true;
};

const handleFavoriteTabBeforeChange = (tabId: string): boolean => {
  if (props.userPlatform === 'direct' && tabId === 'considering') {
    openAgentServiceGuideModal();
    return false;
  }
  if (props.userPlatform === 'agent' && tabId === 'favorites') {
    openDirectModal();
    return false;
  }
  return true;
};

const handleActionSelect = (key: ActionKey) => {
  const actionMap: Record<ActionKey, () => void> = {
    basicInfo: () => {
      isRegistrationModalOpen.value = true;
    },
    jobEntry: () => {
      openDirectModal();
    },
    agentService: () => {
      openAgentServiceGuideModal();
    },
    agentInterview: () => {
      isAgentInterviewGuideModalOpen.value = true;
    },
  };

  actionMap[key]?.();
};
</script>

<style module lang="scss">
@import '@/assets/stylesheets/components/_variables.scss';

.sectionLayout {
  gap: 2.5rem;
}

.platformSections {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.platformSection {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.actionListWrapper {
  width: 100%;
  box-sizing: border-box;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  justify-content: center;
}

@media (min-width: 769px) {
  .actionListWrapper {
    padding-left: 24px;
    padding-right: 24px;
  }
}

/* TODO: shared/loading が作成されたら共通化する - 選考管理セクション用ローディング（青背景のため白スピナー） */
.sectionLoading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  width: 100%;
}

.sectionLoadingSpinner {
  display: inline-block;
  width: 5rem;
  height: 5rem;
  vertical-align: -0.125em;
  border: 0.4em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  color: #fff;
  animation: section-loading-spin 0.75s linear infinite;
}

@keyframes section-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

.agentRecommendLoading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  width: 100%;
}

.agentRecommendLoadingSpinner {
  display: inline-block;
  width: 5rem;
  height: 5rem;
  vertical-align: -0.125em;
  border: 0.4em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  color: $primary-blue;
  animation: section-loading-spin 0.75s linear infinite;
}

.directAgentRecommendCta {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 640px;
  box-sizing: border-box;
  background-color: $white-01;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
}

@media (max-width: 768px) {
  .directAgentRecommendCta {
    width: calc(100% - 2rem);
    padding: 2rem 1rem;
  }
}

.directAgentRecommendBody {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
}

.directAgentRecommendHint {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #545454;
}
</style>
